/**
 * The design round an RCS template's text: a plain message, a card, or a
 * carousel, and the suggested replies and actions under it or on its cards.
 *
 * The text itself stays in the template dialog's text box, slots and all; this
 * is everything else (`rcs-design.ts` says where the text goes per layout).
 */

import { Plus, Trash2 } from 'lucide-react';
import { Button, Input, Select } from '@connect/web-shared';

import {
  LIMITS,
  SUGGESTION_TYPES,
  emptyCard,
  type Card,
  type RcsDesign,
  type Suggestion,
  type SuggestionType,
} from './rcs-design';

/** An instant as a datetime-local input shows it, in the viewer's own zone. */
function localInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function instant(local: string): string | null {
  if (!local) return null;
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function SuggestionRow({
  item,
  onChange,
  onRemove,
}: {
  item: Suggestion;
  onChange: (next: Suggestion) => void;
  onRemove: () => void;
}) {
  const set = (patch: Partial<Suggestion>) => onChange({ ...item, ...patch });
  return (
    <div className="space-y-2 rounded-control border border-line p-2.5">
      <div className="flex flex-wrap items-end gap-2">
        <Select
          size="sm"
          label="Kind"
          containerClassName="w-48"
          options={SUGGESTION_TYPES}
          value={item.type}
          onChange={(e) => onChange({ type: e.target.value as SuggestionType, text: item.text })}
        />
        <Input
          size="sm"
          label="Label"
          containerClassName="min-w-40 flex-1"
          maxLength={LIMITS.suggestion}
          value={item.text}
          onChange={(e) => set({ text: e.target.value })}
        />
        <Button size="sm" variant="ghost" aria-label="Remove suggestion" onClick={onRemove}>
          <Trash2 className="size-3.5" />
        </Button>
      </div>
      {item.type === 'dial' || item.type === 'compose' ? (
        <Input size="sm" label="Phone number" placeholder="+919876543210" value={item.phone ?? ''}
          onChange={(e) => set({ phone: e.target.value.trim() })} />
      ) : null}
      {item.type === 'compose' ? (
        <Input size="sm" label="Message they are offered to send" value={item.message ?? ''}
          onChange={(e) => set({ message: e.target.value })} />
      ) : null}
      {item.type === 'open_url' ? (
        <div className="grid gap-2 sm:grid-cols-[1fr_12rem]">
          <Input size="sm" label="Address" placeholder="https://" value={item.url ?? ''}
            onChange={(e) => set({ url: e.target.value.trim() })} />
          <Select size="sm" label="Opens in" value={item.open_in ?? 'browser'}
            options={[
              { value: 'browser', label: 'The browser' },
              { value: 'webview_full', label: 'In the chat — full' },
              { value: 'webview_tall', label: 'In the chat — tall' },
              { value: 'webview_half', label: 'In the chat — half' },
            ]}
            onChange={(e) => set({ open_in: e.target.value as Suggestion['open_in'] })} />
        </div>
      ) : null}
      {item.type === 'view_location' ? (
        <div className="grid gap-2 sm:grid-cols-3">
          <Input size="sm" label="Search (or a point)" placeholder="Business name, area" value={item.query ?? ''}
            onChange={(e) => set({ query: e.target.value })} />
          <Input size="sm" label="Latitude" inputMode="decimal" value={item.latitude ?? ''}
            onChange={(e) => set({ latitude: e.target.value === '' ? null : Number(e.target.value) })} />
          <Input size="sm" label="Longitude" inputMode="decimal" value={item.longitude ?? ''}
            onChange={(e) => set({ longitude: e.target.value === '' ? null : Number(e.target.value) })} />
        </div>
      ) : null}
      {item.type === 'calendar' ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <Input size="sm" label="Event title" value={item.title ?? ''} onChange={(e) => set({ title: e.target.value })} />
          <Input size="sm" label="Details" value={item.description ?? ''} onChange={(e) => set({ description: e.target.value })} />
          <Input size="sm" type="datetime-local" label="Starts" value={localInput(item.start)}
            onChange={(e) => set({ start: instant(e.target.value) })} />
          <Input size="sm" type="datetime-local" label="Ends" value={localInput(item.end)}
            onChange={(e) => set({ end: instant(e.target.value) })} />
        </div>
      ) : null}
      {item.type !== 'reply' ? (
        <Input size="sm" label="If the phone cannot do it, open (optional)" placeholder="https://"
          value={item.fallback_url ?? ''} onChange={(e) => set({ fallback_url: e.target.value.trim() })} />
      ) : null}
    </div>
  );
}

function Suggestions({
  items,
  max,
  title,
  onChange,
}: {
  items: Suggestion[];
  max: number;
  title: string;
  onChange: (next: Suggestion[]) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-muted">
          {title} <span className="text-faint">· {items.length} of {max}</span>
        </p>
        {items.length < max ? (
          <Button size="sm" variant="ghost" onClick={() => onChange([...items, { type: 'reply', text: '' }])}>
            <Plus className="size-3.5" />
            Add
          </Button>
        ) : null}
      </div>
      {items.map((item, index) => (
        <SuggestionRow
          key={index}
          item={item}
          onChange={(next) => onChange(items.map((s, i) => (i === index ? next : s)))}
          onRemove={() => onChange(items.filter((_, i) => i !== index))}
        />
      ))}
    </div>
  );
}

function CardEditor({
  card,
  onChange,
  textHere,
}: {
  card: Card;
  onChange: (next: Card) => void;
  /** The template's text is this card's description. */
  textHere: boolean;
}) {
  const set = (patch: Partial<Card>) => onChange({ ...card, ...patch });
  return (
    <div className="space-y-2">
      <Input size="sm" label="Title (optional)" maxLength={LIMITS.title} value={card.title ?? ''}
        onChange={(e) => set({ title: e.target.value })} />
      {textHere ? (
        <p className="text-2xs text-faint">The description is the template's text, with each recipient's details in it.</p>
      ) : (
        <Input size="sm" label="Description" maxLength={LIMITS.description} value={card.description ?? ''}
          onChange={(e) => set({ description: e.target.value })} />
      )}
      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_9rem]">
        <Input size="sm" label="Picture, GIF, video or PDF (https)" placeholder="https://"
          value={card.media?.url ?? ''}
          onChange={(e) => {
            const url = e.target.value.trim();
            set({ media: url ? { height: card.media?.height ?? 'MEDIUM', thumbnail_url: card.media?.thumbnail_url ?? null, url } : null });
          }} />
        <Input size="sm" label="Thumbnail (optional)" placeholder="https://" disabled={!card.media}
          value={card.media?.thumbnail_url ?? ''}
          onChange={(e) => card.media && set({ media: { ...card.media, thumbnail_url: e.target.value.trim() || null } })} />
        <Select size="sm" label="Height" disabled={!card.media} value={card.media?.height ?? 'MEDIUM'}
          options={[
            { value: 'SHORT', label: 'Short' },
            { value: 'MEDIUM', label: 'Medium' },
            { value: 'TALL', label: 'Tall' },
          ]}
          onChange={(e) => card.media && set({ media: { ...card.media, height: e.target.value as 'SHORT' | 'MEDIUM' | 'TALL' } })} />
      </div>
      <Suggestions title="Buttons on the card" max={LIMITS.cardSuggestions} items={card.suggestions}
        onChange={(suggestions) => set({ suggestions })} />
    </div>
  );
}

/** An SMS template the client may fall back on: as many slots as this message. */
export interface FallbackOption {
  public_id: string;
  name: string;
  variable_count: number;
}

export function RcsDesignEditor({
  design,
  onChange,
  slots,
  fallbacks,
}: {
  design: RcsDesign;
  onChange: (next: RcsDesign) => void;
  /** How many {#var#} slots the message's text has. */
  slots: number;
  /** The client's SMS templates; only those with as many slots are offered. */
  fallbacks: FallbackOption[];
}) {
  const set = (patch: Partial<RcsDesign>) => onChange({ ...design, ...patch });
  const fitting = fallbacks.filter((f) => f.variable_count === slots);
  const chosen = fallbacks.find((f) => f.public_id === design.sms_fallback);
  return (
    <div className="space-y-4 rounded-control border border-line p-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <Select
          label="Layout"
          value={design.layout}
          options={[
            { value: 'text', label: 'Text message' },
            { value: 'card', label: 'Rich card' },
            { value: 'carousel', label: 'Carousel of cards' },
          ]}
          onChange={(e) => {
            const layout = e.target.value as RcsDesign['layout'];
            set({
              layout,
              card: layout === 'card' ? (design.card ?? emptyCard()) : design.card,
              cards: layout === 'carousel' && design.cards.length < 2 ? [emptyCard(), emptyCard()] : design.cards,
            });
          }}
        />
        {design.layout === 'card' ? (
          <Select label="Card" value={design.orientation}
            options={[
              { value: 'VERTICAL', label: 'Picture above' },
              { value: 'HORIZONTAL', label: 'Picture beside' },
            ]}
            onChange={(e) => set({ orientation: e.target.value as RcsDesign['orientation'] })} />
        ) : null}
        {design.layout === 'card' && design.orientation === 'HORIZONTAL' ? (
          <Select label="Picture on the" value={design.image_alignment}
            options={[
              { value: 'LEFT', label: 'Left' },
              { value: 'RIGHT', label: 'Right' },
            ]}
            onChange={(e) => set({ image_alignment: e.target.value as RcsDesign['image_alignment'] })} />
        ) : null}
        {design.layout === 'carousel' ? (
          <Select label="Card width" value={design.card_width}
            options={[
              { value: 'MEDIUM', label: 'Medium' },
              { value: 'SMALL', label: 'Small' },
            ]}
            onChange={(e) => set({ card_width: e.target.value as RcsDesign['card_width'] })} />
        ) : null}
        <Input label="Keep trying for (hours)" inputMode="numeric" placeholder="Google's default"
          value={design.ttl_hours ?? ''}
          hint="How long Google keeps trying a phone that is off."
          onChange={(e) => set({ ttl_hours: e.target.value ? Number(e.target.value) : null })} />
      </div>

      {design.layout === 'card' && design.card ? (
        <CardEditor card={design.card} textHere onChange={(card) => set({ card })} />
      ) : null}

      {design.layout === 'carousel' ? (
        <div className="space-y-3">
          {design.cards.map((card, index) => (
            <div key={index} className="space-y-2 rounded-control bg-surface-2 p-2.5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-heading">Card {index + 1}</p>
                {design.cards.length > LIMITS.carousel[0] ? (
                  <Button size="sm" variant="ghost" aria-label={`Remove card ${index + 1}`}
                    onClick={() => set({ cards: design.cards.filter((_, i) => i !== index) })}>
                    <Trash2 className="size-3.5" />
                  </Button>
                ) : null}
              </div>
              <CardEditor card={card} textHere={index === 0}
                onChange={(next) => set({ cards: design.cards.map((c, i) => (i === index ? next : c)) })} />
            </div>
          ))}
          {design.cards.length < LIMITS.carousel[1] ? (
            <Button size="sm" variant="secondary" onClick={() => set({ cards: [...design.cards, emptyCard()] })}>
              <Plus className="size-3.5" />
              Add a card
            </Button>
          ) : null}
        </div>
      ) : null}

      <Suggestions title="Suggestions under the message" max={LIMITS.messageSuggestions} items={design.suggestions}
        onChange={(suggestions) => set({ suggestions })} />

      <Select
        label="If the phone cannot receive RCS, send this SMS"
        value={design.sms_fallback ?? ''}
        options={[
          { value: '', label: 'Nothing' },
          ...fitting.map((f) => ({ value: f.public_id, label: f.name })),
        ]}
        error={
          chosen && chosen.variable_count !== slots
            ? `${chosen.name} has ${chosen.variable_count} slot(s); this message has ${slots}.`
            : undefined
        }
        hint={
          fallbacks.length === 0
            ? 'This client has no SMS templates.'
            : `One of the client's DLT templates with ${slots} slot(s): the same values fill it, in order, on its own SMS account.`
        }
        onChange={(e) => set({ sms_fallback: e.target.value || null })}
      />
    </div>
  );
}
